/**
 * resolveReportPresentation — single resolver for template-effective
 * layout consumed by SiteRagReportView, DOCX export, and print/PDF.
 *
 * Live-auto policy: when a report is linked to an active template
 * (`report.templateId === template.id`), section visibility, column
 * layouts, cover layout, and template heading overrides come from the
 * live template — not gen-time snapshots on the report doc.
 * Per-report `headingOverrides` still win on individual keys (user
 * edits in the report viewer).
 */
import type { ReportTypeId } from '../config/reportTypes';
import type { ReportTheme } from '../types/reportTheme';
import type { CoverLayout, ReportTemplate } from '../types/reportTemplate';
import type { SiteRagReport } from '../types/siteRag';
import type { CompanyArchetype } from '../types/company';
import type { ThemeLocale } from './themeFormatters';
import { TWO_COLUMN_PRESET_IDS, getThemePreset } from './reportThemePresets';
import { isEditorial2ColEnabled } from './runtimeFlags';
import { resolveEffectiveTheme } from './mergeTemplateTheme';
import { isDarkPaperColor, DEFAULT_PAGE_BG } from './reportLayoutTokens';
import { REPORT_COVER_DISCLAIMER } from './reportLegalDisclaimer';
import { resolveHeadingChrome, type ResolvedHeadingChrome } from './resolveHeadingChrome';
import {
  resolveEffectiveTemplateConfig,
  resolveSectionColumnLayout,
  resolveSectionVisibility,
  type EffectiveTemplateConfig,
} from './effectiveTemplateConfig';
import { resolveEffectiveSectionOrder } from './effectiveSectionOrder';
import {
  computeTemplatePageBreakPlan,
  type TemplatePageBreakPlan,
} from './templatePages/computeTemplatePageBreaks';
import {
  isSectionApplicableForReportType,
  isSectionEnabledForType,
} from './isSectionEnabledForType';
import { resolveCoverLayout } from './resolveCoverLayout';

export interface ReportPresentationInput {
  report: Pick<
    SiteRagReport,
    | 'reportType'
    | 'sectionOrder'
    | 'sectionVisibility'
    | 'sectionColumnLayouts'
    | 'headingOverrides'
    | 'pageMapOverrides'
    | 'templateId'
    | 'templateVersionUsed'
    | 'companyArchetypeOverride'
    | 'company'
    | 'gridLedStandbyPlanningProjection'
    | 'gridLedStandbyPlanningAtGen'
  > & { coverLayout?: CoverLayout };
  template: ReportTemplate | null | undefined;
  theme: ReportTheme;
  /** Studio preview — replaces template headingOverrides before merge. */
  headingOverridesProp?: Record<string, string>;
  /** Studio preview — resolve layout from supplied template regardless of report.templateId. */
  forceLiveTemplateLayout?: boolean;
  /**
   * RC5 — the LIVE per-report theme override (the highest-precedence `report`
   * tier in `resolveEffectiveTheme`). Threaded through so the dead override tier
   * is wired end-to-end. BYTE-STABLE: when absent/empty the merge takes its
   * reference-identical empty-override fast path, so reports without an override
   * resolve EXACTLY as before. There is no persisted per-report theme field yet,
   * so today this is always absent — the seam is wired for when one lands.
   */
  reportThemeOverride?: Partial<ReportTheme> | null;
  /**
   * #34 P2 — the RENDER SURFACE this presentation is resolved for. The screen
   * viewer resolves `'view'`; the print/PDF/export tree resolves `'export'`.
   * OPTIONAL, default `'view'` — every existing call site (31 of them) is
   * untouched and byte-identical. This is the PRE-POSITIONED seam STAGE 2's
   * render-engine collapse routes the per-surface label-case choice through;
   * STAGE 1 only resolves the {@link ReportPresentation.labelTransform} token
   * from it, and that token's defaults reproduce today's classes EXACTLY (see
   * `labelTransform` below), so NOTHING renders differently in this stage.
   */
  surface?: 'view' | 'export';
}

/** The un-merged theme-preset id surfaced on the contract (RC4). */
export type ResolvedPresetId = string | null;

/**
 * ResolvedTheme — the fully-merged theme object every renderer reads as
 * the SINGLE source for colors + typography. It is just a `ReportTheme`
 * (no shape change) but its name documents the contract: it is the
 * `resolveEffectiveTheme` output (preset → store delta → template.theme →
 * report overrides), so a renderer that reads `presentation.theme` can
 * NEVER re-derive a divergent palette/typography from the raw store theme
 * or the raw template. Exposed as both `theme` and convenience scalar
 * tokens (heading/body font + primary/heading/body colors) on the
 * presentation so the parity gate can assert token identity directly.
 */
export type ResolvedTheme = ReportTheme;

/** Resolved figure-caption prefix ("Figure ", "Fig. ", …). */
export type FigureNumberingPrefix = string;

/** Resolved diagonal-watermark state, normalised for every renderer. */
export interface ResolvedWatermark {
  enabled: boolean;
  text: string;
  color: string;
  /** 0–1 opacity multiplier on top of the color alpha. */
  opacity: number;
}

export interface ReportPresentation {
  template: ReportTemplate | null;
  templateConfig: EffectiveTemplateConfig | null;
  sectionOrder: string[];
  /** Effective visibility map for TOC (template-first when live). */
  effectiveVisibility: Record<string, boolean>;
  isVisible: (sectionId: string) => boolean;
  columnLayout: (sectionId: string) => 1 | 2 | 3;
  /** Shared BODY column default (1 or 2) — resolved via {@link resolveBodyColumns}. */
  bodyColumns: 1 | 2;
  headingOverrides: Record<string, string>;
  coverLayout: CoverLayout;
  /**
   * RC4 — the UN-MERGED theme-preset id (`template.themePreset`), surfaced so
   * renderers can drive identity-dependent chrome (Monograph framing, the
   * DOCX 7-way cover switch, the figure-prefix separator) off the preset id
   * directly instead of sniffing the merged heading-font string. `null` when
   * no preset is in play (legacy / no-preset reports) — so absence-of-presetId
   * means "legacy" everywhere, byte-stable.
   */
  presetId: ResolvedPresetId;
  /**
   * RC3 — the resolved page BACKGROUND colour (paper tint). Surfaced as a
   * first-class token so the screen frame, the `.pagedjs_page` surface, and the
   * DOCX/PPTX page fill all read the SAME paper colour off the contract instead
   * of each re-reading `theme.backgroundColor`.
   *
   * W4 BLACK-PAGES FIX: this is the merged `theme.backgroundColor` GUARDED to a
   * light-paper colour ({@link resolvePaperBackground}) — a report page is
   * never dark, so a dark app/store theme background (inherited as the report
   * theme's base in the live viewer) is replaced by the active preset's paper
   * tint, else white. When the merged background is already light (the dominant
   * case) it equals `theme.backgroundColor` verbatim (byte-stable).
   */
  backgroundColor: string;
  /**
   * RC3/RC5 — the resolved section-heading treatment (size step / case /
   * tracking / rule weight + colour) keyed on `theme.headingScale`. ONE source
   * the screen, DOCX, and PPTX all read so the heading treatment can never
   * diverge by medium. See {@link resolveHeadingChrome}.
   */
  headingChrome: ResolvedHeadingChrome;
  disclaimer: string;
  pageBreakPlan: TemplatePageBreakPlan;
  /** True when live template.version is newer than report.templateVersionUsed. */
  templateStale: boolean;
  usesLiveTemplateLayout: boolean;

  // ── SINGLE RESOLVED LOOK CONTRACT (template-consistency) ──────────
  /**
   * The fully-merged effective theme — preset (themePreset → palette +
   * typography) UNDER explicit template `theme` overrides, over the store
   * delta, with report overrides on top. THIS is the only theme a renderer
   * should read: screen/PDF/DOCX/PPTX/preview all consume `presentation.theme`
   * so colors + typography can never drift between media.
   */
  theme: ResolvedTheme;
  /** Resolved heading font family (CSS stack). Convenience for parity asserts. */
  headingFont: string;
  /** Resolved body font family (CSS stack). */
  bodyFont: string;
  /** Resolved display/cover font family (falls back to headingFont). */
  displayFont: string;
  /** Resolved type scale multiplier (0.875 | 1 | 1.125 | 1.25). */
  typographyScale: 0.875 | 1 | 1.125 | 1.25;
  /** Resolved primary/accent color. */
  primaryColor: string;
  /** Resolved heading color. */
  headingColor: string;
  /** Resolved body-text color. */
  bodyTextColor: string;
  /** Resolved figure-caption prefix ("Figure ", "Fig. ", …). */
  figureNumberingPrefix: FigureNumberingPrefix;
  /** Resolved running-footer page-number format. */
  pageNumberFormat: ReportTheme['pageNumberFormat'];
  /** Resolved diagonal-watermark state (always defined). */
  watermark: ResolvedWatermark;
  /** Resolved locale & unit preferences (always filled with defaults). */
  locale: Required<ThemeLocale>;
  /** Resolved ordered chart-series palette. */
  chartPalette: string[];
  /**
   * #34 P2 — the resolved CASE TRANSFORM for eyebrow/field labels (the small
   * uppercase meta lines above narrative blocks). `'uppercase'` reproduces the
   * historical literal `uppercase tracking-widest` chrome; `'none'` lets the
   * label keep its source case. Renderers derive their label class from this
   * token via {@link import('./reportTypography').labelClass} instead of
   * hardcoding the literal.
   *
   * BYTE-STABLE: this defaults to `'uppercase'` for EVERY surface in STAGE 1, so
   * the chrome is identical to today on view AND export. It is the
   * PRE-POSITIONED chokepoint STAGE 2's collapse routes through — only then does
   * an `'export'` surface resolve `'none'` (de-uppercasing the 14 hand-inline
   * strips) take visible effect. POLISH-1 (followup) extended the de-uppercase
   * to the shared constants (`REPORT_FIELD_LABEL_CLASS`, the meta-chip, the NEPA
   * title) too: each now has an audience-aware builder in `reportTypography.tsx`
   * that resolves its case chrome from `useViewMode` (export → `'none'`), so they
   * match the 14 strips on export while staying byte-identical on view.
   */
  labelTransform: 'uppercase' | 'none';
}

/**
 * resolveBodyColumns — the SINGLE source of truth for the report BODY column
 * count (1 or 2), shared by the on-screen viewer (SiteRagReportView),
 * PaginatedReportView, and the DOCX export. Replaces the three previously
 * divergent reads (preset-id in the viewer, a blanket `.template-export-cols-2`
 * PDF selector, and a hardcoded section allow-list in DOCX).
 *
 * PRECEDENCE (highest → lowest):
 *   1. Explicit template-editor override — when a template carries a `themePreset`
 *      it IS the editor's column choice and is AUTHORITATIVE: a two-column
 *      academic preset (Quarterly / Review / Proceedings, per
 *      `TWO_COLUMN_PRESET_IDS`) ⇒ 2; every single-column preset ⇒ 1. Reading the
 *      preset id directly (not the merged `theme.bodyColumns`) avoids a stale
 *      store `bodyColumns: 2` keeping a 1-column preset at two columns (#135/#163).
 *   2. Legacy fallback — a pre-preset (no `themePreset`) template defers to the
 *      resolved `theme.bodyColumns` (2 ⇒ 2, else 1), so legacy reports are
 *      byte-stable.
 *
 * NB: this is the BODY DEFAULT only. Per-section template/report overrides +
 * the `supportsColumnLayout` registry guard still win inside
 * `resolveLiveSectionColumnLayout` / `resolveSectionPageColumnCountForSection`.
 */
export function resolveBodyColumns(
  template: Pick<ReportTemplate, 'themePreset'> | null | undefined,
  theme: Pick<ReportTheme, 'bodyColumns'>,
): 1 | 2 {
  // Part L2 — when the editorial TWO-COLUMN body is engaged (the editorial flag
  // AND the 2-col sub-flag), the body defaults to 2 columns under EVERY palette
  // preset (the editorial structure includes the 2-col body; the chosen palette
  // supplies colour only). This is the SINGLE authority the viewer, export host,
  // and DOCX all call, so they engage the 2-col page-flow box + post-layout
  // reflow in lock-step. Returns BEFORE the preset/theme branches so a 1-col
  // palette can't keep it at one column. With the sub-flag OFF (the default and
  // the 1-col fallback) this is skipped and column resolution is byte-identical.
  if (isEditorial2ColEnabled()) return 2;
  const presetId = template?.themePreset;
  if (presetId) {
    return (TWO_COLUMN_PRESET_IDS as readonly string[]).includes(presetId) ? 2 : 1;
  }
  return theme.bodyColumns === 2 ? 2 : 1;
}

/** Default locale used when a theme leaves `themeLocale` unset — mirrors
 *  the formatter defaults (US-EN / YMD / SI metric) so legacy reports
 *  format byte-identically. */
const DEFAULT_PRESENTATION_LOCALE: Required<ThemeLocale> = {
  dateFormat: 'YMD',
  numberFormat: '1,234.56',
  temperatureUnit: 'C',
  distanceUnit: 'metric',
  energyUnit: 'kWh-MWh',
};

/** Default chart palette — matches DEFAULT_THEME.chartPalette so an absent
 *  palette renders byte-identically. */
const DEFAULT_CHART_PALETTE = ['#0071df', '#ffab00', '#1D9E75', '#A32D2D', '#7F77DD'];

/**
 * resolveFigureNumberingPrefix — the figure-caption prefix, normalised to a
 * non-empty string. Trims to the bare prefix while preserving a trailing
 * space the tenant included (e.g. "Figure "). Absent/empty ⇒ "Figure ".
 */
export function resolveFigureNumberingPrefix(theme: Pick<ReportTheme, 'figureNumberingPrefix'>): FigureNumberingPrefix {
  const raw = theme.figureNumberingPrefix;
  return typeof raw === 'string' && raw.length > 0 ? raw : 'Figure ';
}

/**
 * resolveWatermark — normalise `theme.themeWatermark` into a fully-defined
 * {@link ResolvedWatermark} so every renderer reads the SAME enabled/text/
 * color/opacity (no per-medium defaulting drift). Absent ⇒ disabled with
 * the historical DRAFT defaults.
 */
export function resolveWatermark(theme: Pick<ReportTheme, 'themeWatermark'>): ResolvedWatermark {
  const wm = theme.themeWatermark;
  const opacityRaw = typeof wm?.opacity === 'number' && Number.isFinite(wm.opacity) ? wm.opacity : 0.08;
  return {
    enabled: wm?.enabled === true,
    text: (typeof wm?.text === 'string' && wm.text.trim()) ? wm.text : 'DRAFT',
    color: (typeof wm?.color === 'string' && wm.color.trim()) ? wm.color : '#dc2626',
    opacity: Math.min(1, Math.max(0, opacityRaw)),
  };
}

/**
 * resolvePresentationLocale — fill `theme.themeLocale` with the formatter
 * defaults so every renderer formats dates/numbers/units identically.
 */
export function resolvePresentationLocale(theme: Pick<ReportTheme, 'themeLocale'>): Required<ThemeLocale> {
  return { ...DEFAULT_PRESENTATION_LOCALE, ...(theme.themeLocale ?? {}) };
}

/**
 * resolveTypographyScale — clamp the type-scale multiplier to the documented
 * set; any out-of-range / missing value falls back to 1 (no scaling).
 */
export function resolveTypographyScale(
  theme: Pick<ReportTheme, 'themeTypographyScale'>,
): 0.875 | 1 | 1.125 | 1.25 {
  const ts = theme.themeTypographyScale;
  return ts === 0.875 || ts === 1 || ts === 1.125 || ts === 1.25 ? ts : 1;
}

/**
 * resolvePaperBackground — the report PAGE (paper) background, guaranteed to
 * be a LIGHT-paper colour.
 *
 * W4 BLACK-PAGES REGRESSION FIX. A report is ALWAYS a light-paper document,
 * independent of the app's dark/light mode. In the LIVE viewer the report
 * theme inherits the store/app theme as its base via `resolveEffectiveTheme`;
 * when that base is DARK and there is no light-paper preset overriding the
 * background (the legacy / no-preset path, OR a dark store-delta that beats a
 * preset), the merged `theme.backgroundColor` is DARK — which previously drove
 * the page surface BLACK.
 *
 * The page background must NOT inherit a dark app/store value. Precedence:
 *   1. The merged theme background when it is light paper (the legitimate
 *      preset/template/report paper tint — journal #fbfaf6, monograph
 *      #fcfcfb, review #fcfbf8, white, …).
 *   2. Otherwise the active PRESET's paper tint when light — so a light
 *      academic preset shows its warm paper even when the store/app theme is
 *      dark (a dark store backgroundColor is never a valid report paper).
 *   3. Otherwise white paper ({@link DEFAULT_PAGE_BG}).
 *
 * Byte-stable: when the merged theme background is already light (the
 * dominant case — DEFAULT_THEME is white, every preset paper tint is light)
 * step 1 returns it verbatim, so no current report changes colour. The
 * downstream `resolvePageBg` carries the same guard as defence-in-depth.
 */
export function resolvePaperBackground(
  mergedBackgroundColor: string,
  presetId: string | null | undefined,
): string {
  if (!isDarkPaperColor(mergedBackgroundColor)) return mergedBackgroundColor;
  const presetBg = getThemePreset(presetId)?.backgroundColor;
  if (presetBg && !isDarkPaperColor(presetBg)) return presetBg;
  return DEFAULT_PAGE_BG;
}

/** Whether layout fields should resolve from the live template, not report snapshots. */
export function shouldUseLiveTemplateLayout(
  report: Pick<SiteRagReport, 'templateId'>,
  template: ReportTemplate | null | undefined,
): boolean {
  if (!template) return false;
  if (report.templateId) return report.templateId === template.id;
  // Archetype seed / Studio draft without a persisted report link.
  return true;
}

export function resolveLiveSectionVisibility(
  sectionId: string,
  report: Pick<SiteRagReport, 'sectionVisibility'>,
  templateConfig: EffectiveTemplateConfig | null | undefined,
  liveLayout: boolean,
): boolean {
  if (liveLayout && templateConfig) {
    const tmplLevel = templateConfig.visibility[sectionId];
    if (typeof tmplLevel === 'boolean') return tmplLevel;
    return true;
  }
  return resolveSectionVisibility(sectionId, report.sectionVisibility, templateConfig);
}

export function resolveLiveSectionColumnLayout(
  sectionId: string,
  report: Pick<SiteRagReport, 'sectionColumnLayouts'>,
  templateConfig: EffectiveTemplateConfig | null | undefined,
  liveLayout: boolean,
  themeBodyColumns?: 1 | 2,
): 1 | 2 | 3 {
  if (liveLayout && templateConfig) {
    const tmplLevel = templateConfig.columnLayouts[sectionId];
    if (tmplLevel === 1 || tmplLevel === 2 || tmplLevel === 3) return tmplLevel;
    // No explicit per-section layout ⇒ fall back to the theme's bodyColumns
    // DEFAULT (two-column academic presets set this to 2). A user's explicit
    // section override above still wins.
    if (themeBodyColumns === 2) return 2;
    return 1;
  }
  return resolveSectionColumnLayout(
    sectionId,
    report.sectionColumnLayouts,
    templateConfig,
    themeBodyColumns,
  );
}

function mergeHeadingOverrides(
  templateOverrides: Record<string, string>,
  reportOverrides: Record<string, string> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(templateOverrides)) {
    if (v && v.trim()) out[k] = v;
  }
  for (const [k, v] of Object.entries(reportOverrides ?? {})) {
    if (v && v.trim()) out[k] = v;
  }
  return out;
}

function buildEffectiveVisibilityMap(
  sectionOrder: readonly string[],
  report: Pick<SiteRagReport, 'reportType' | 'sectionVisibility' | 'sectionOrder' | 'templateId'>,
  templateConfig: EffectiveTemplateConfig | null | undefined,
  liveLayout: boolean,
  template: ReportTemplate | null,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  const ids = sectionOrder.length > 0
    ? sectionOrder
    : (templateConfig?.sectionIds ?? []);
  for (const id of ids) {
    out[id] = isSectionEnabledForType(id, report, templateConfig, template);
  }
  if (templateConfig?.visibility) {
    for (const [id, vis] of Object.entries(templateConfig.visibility)) {
      if (!(id in out)) {
        out[id] = isSectionApplicableForReportType(id, report)
          && (liveLayout ? !!vis : resolveSectionVisibility(id, report.sectionVisibility, templateConfig));
      }
    }
  }
  return out;
}

export function resolveReportPresentation(input: ReportPresentationInput): ReportPresentation {
  const { report, theme: baseTheme, headingOverridesProp, forceLiveTemplateLayout, reportThemeOverride } = input;
  const template = input.template ?? null;
  const reportType = (report.reportType ?? 'feasibility') as ReportTypeId;
  const usesLiveTemplateLayout = forceLiveTemplateLayout
    ?? shouldUseLiveTemplateLayout(report, template);

  // ── SINGLE THEME SEAM ─────────────────────────────────────────────
  // Fold the template's PRESET (palette + typography) UNDER its explicit
  // `theme` editor overrides — the one canonical merge — so EVERY renderer
  // reads the same effective theme off `presentation.theme`. `resolveEffectiveTheme`
  // is idempotent over an already-resolved theme (a field the resolved theme
  // already carries beats the preset via the store-delta step), so the
  // on-screen viewer (which pre-resolves the theme before calling this) and a
  // raw-store caller (DOCX/PPTX/print bundle) converge on the SAME object.
  // When `template.themePreset` is absent this reduces EXACTLY to
  // `mergeTemplateTheme(baseTheme, template)`, so legacy reports are byte-stable.
  // RC5 — thread the live per-report override tier. Absent/empty ⇒ the merge's
  // reference-identical empty-override fast path (byte-stable).
  const theme = resolveEffectiveTheme({ base: baseTheme, template, report: reportThemeOverride });

  const templateConfig = template
    ? resolveEffectiveTemplateConfig(template, reportType, report.sectionOrder)
    : null;

  const sectionOrder = resolveEffectiveSectionOrder({
    reportType,
    sectionOrder: report.sectionOrder,
    template: template ?? undefined,
    report,
  });

  const effectiveVisibility = buildEffectiveVisibilityMap(
    sectionOrder,
    report,
    templateConfig,
    usesLiveTemplateLayout,
    template,
  );

  const isVisible = (sectionId: string) =>
    isSectionEnabledForType(sectionId, report, templateConfig, template);

  // The body column default — resolved via the SHARED `resolveBodyColumns`
  // (preset-id authority, else legacy `theme.bodyColumns`). Per-section
  // template/report overrides still win inside resolveLiveSectionColumnLayout;
  // this only sets the fallback. The viewer + DOCX read the same value off the
  // returned presentation (`bodyColumns`), so the three engines never diverge.
  const bodyColumns = resolveBodyColumns(template, theme);
  // NB: the per-section fallback uses the resolved body default. (Legacy: this
  // previously read `theme.bodyColumns` directly; now both agree via the shared
  // resolver, so the preset-id authority applies to per-section fallbacks too.)
  const themeBodyColumns: 1 | 2 = bodyColumns;

  const columnLayout = (sectionId: string) =>
    resolveLiveSectionColumnLayout(
      sectionId,
      report,
      templateConfig,
      usesLiveTemplateLayout,
      themeBodyColumns,
    );

  const templateHeadingOverrides = usesLiveTemplateLayout
    ? (headingOverridesProp
      ?? templateConfig?.headingOverrides
      ?? template?.headingOverrides
      ?? {})
    : (headingOverridesProp ?? {});

  const headingOverrides = mergeHeadingOverrides(
    templateHeadingOverrides,
    report.headingOverrides,
  );

  const coverLayout = resolveCoverLayout(
    report,
    template,
    templateConfig,
    theme,
    usesLiveTemplateLayout,
  );

  // RC5 — per-archetype disclaimer. The report's effective archetype
  // (per-report override beats the company snapshot) selects an
  // archetype-specific disclaimer when the theme carries one
  // (`disclaimerByArchetype[archetype]`); otherwise we keep the canonical
  // REPORT_COVER_DISCLAIMER so reports WITHOUT an archetype-specific override
  // are byte-stable. (NB: `footerSubtext` is intentionally NOT used as a
  // silent fallback — DEFAULT_THEME.footerSubtext is always non-empty, so it
  // would regress EVERY legacy report's cover disclaimer. See open concerns.)
  const archetype: CompanyArchetype | undefined =
    report.companyArchetypeOverride ?? report.company?.archetype;
  const archetypeDisclaimer = archetype
    ? theme.disclaimerByArchetype?.[archetype]
    : undefined;
  const disclaimer = (typeof archetypeDisclaimer === 'string' && archetypeDisclaimer.trim())
    ? archetypeDisclaimer
    : REPORT_COVER_DISCLAIMER;

  const pageBreakPlan = computeTemplatePageBreakPlan({
    template,
    reportType,
    sectionOrder,
    sectionVisibility: usesLiveTemplateLayout
      ? effectiveVisibility
      : report.sectionVisibility,
    pageMapOverrides: report.pageMapOverrides,
  });

  const templateVersion = template?.version ?? 1;
  const templateStale =
    !!report.templateId
    && report.templateId === template?.id
    && typeof report.templateVersionUsed === 'number'
    && templateVersion > report.templateVersionUsed;

  // ── Derived look tokens — ALL from the single resolved `theme`, so no
  //    renderer re-derives a divergent value. ───────────────────────
  const figureNumberingPrefix = resolveFigureNumberingPrefix(theme);
  const watermark = resolveWatermark(theme);
  const locale = resolvePresentationLocale(theme);
  const typographyScale = resolveTypographyScale(theme);
  const pageNumberFormat = theme.pageNumberFormat;
  const chartPalette = Array.isArray(theme.chartPalette) && theme.chartPalette.length > 0
    ? theme.chartPalette
    : DEFAULT_CHART_PALETTE;
  const headingFont = theme.headingFont;
  const bodyFont = theme.bodyFont;
  const displayFont = theme.displayFont || theme.headingFont;
  const primaryColor = theme.primaryColor;
  const headingColor = theme.headingColor;
  const bodyTextColor = theme.bodyTextColor;
  // RC4 — the un-merged preset id (NOT the merged theme), so renderers read
  // identity off the editor's choice directly. `null` ⇒ legacy / no-preset.
  const presetId: ResolvedPresetId = template?.themePreset ?? null;
  // RC3 — paper tint as a first-class token. W4 BLACK-PAGES FIX: a report
  // page is ALWAYS light paper, so this is guarded NEVER to be the dark
  // app/store theme background that the report theme inherited as its base.
  // When the merged theme background is light it passes through verbatim
  // (byte-stable — the dominant case); when it is dark the active preset's
  // paper tint shows through, else white. See {@link resolvePaperBackground}.
  const backgroundColor = resolvePaperBackground(theme.backgroundColor, presetId);
  // RC3/RC5 — the one heading treatment, keyed on headingScale.
  const headingChrome = resolveHeadingChrome(theme);
  // #34 P2 — the eyebrow/field-label case transform, resolved per render
  // SURFACE. STAGE 2 ACTIVATES the per-surface choice the STAGE 1 seam
  // pre-positioned: the in-app `'view'` surface keeps the historical
  // `'uppercase'` chrome (byte-identical to today), while the `'export'`
  // surface resolves `'none'` so the 14 hand-inline eyebrow strips
  // de-uppercase on the unified export render. This is the SINGLE chokepoint
  // the render-engine collapse routes those strips through (via
  // `labelClass(presentation.labelTransform)`). POLISH-1 (followup) extended the
  // de-uppercase to the SHARED constants (REPORT_FIELD_LABEL_CLASS / meta-chip /
  // NEPA title): they now resolve the SAME export→'none' rule via their own
  // audience-aware builders + `useViewMode` (see reportTypography.tsx). The
  // default (no `surface`) stays `'view'` → `'uppercase'`, so all 31 legacy call sites are
  // unchanged. Only the unified renderer's `surface='export'` resolve flips to
  // `'none'`, and the legacy export fork never reads this token, so this change
  // is inert until the unified path is taken (flag-gated, reversible).
  const surface: 'view' | 'export' = input.surface ?? 'view';
  const labelTransform: 'uppercase' | 'none' = surface === 'export' ? 'none' : 'uppercase';

  return {
    template,
    templateConfig,
    sectionOrder,
    effectiveVisibility,
    isVisible,
    columnLayout,
    bodyColumns,
    headingOverrides,
    coverLayout,
    presetId,
    backgroundColor,
    headingChrome,
    disclaimer,
    pageBreakPlan,
    templateStale,
    usesLiveTemplateLayout,
    // single resolved look contract
    theme,
    headingFont,
    bodyFont,
    displayFont,
    typographyScale,
    primaryColor,
    headingColor,
    bodyTextColor,
    figureNumberingPrefix,
    pageNumberFormat,
    watermark,
    locale,
    chartPalette,
    labelTransform,
  };
}
